import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AlertController, NavController } from '@ionic/angular';
import { first, Observable, switchMap, take } from 'rxjs';

import { AUTH_TOKEN } from '../../common/auth/auth-token.interceptor';
import { AuthService } from '../../common/auth/auth.service';
import { AbstractRequestFormAdaptor } from '../../common/form/abstract-request-form.adaptor';
import { ProfileFetchAdaptor } from '../profile/profile.adaptor';
// import { WarnUnloadService } from '../warn-unload.service';

export enum AssessmentStatus {
  PENDING_COMPLETION = 'pending_completion',
  PENDING_RESULTS = 'pending_results',
  RECOMMENDATIONS_GENERATED = 'recommendations_generated',
  EXPIRED = 'expired',
  IN_PROGRESS = 'in_progress',
}

export interface ScreeningToolForm {
  assessment_id: number;
  distress_score: number;
  other_comment: string;
  top_topic_ids: number[];
  top_comment_ids: string[];
  respondent_role: string;
  relationship_to_survivor: string;
  assessment_sections_attributes: ScreeningToolFormAssessmentSection[];
  completed_page?: number;
  submit?: boolean;
}

export interface ScreeningToolFormAssessmentSection {
  id: number | null;
  section_id: number;
  comment: string;
  topic_ids: number[];
}

export interface AssessmentFormResponse {
  id: number;
  name: string;
  submission_date: string;
  expiration_date: string;
  completed_date: string;
  started_date: string;
  created_date: string;
  status: AssessmentStatus;
  other_comment: string | null;
  distress_score: number | null;
  top_topic_ids: number[];
  top_comment_ids: string[];
  top_needs: string[];
  respondent_role: string;
  relationship_to_survivor: string;
  assessment_sections: AssessmentSection[];
  completed_page: number;
}

interface AssessmentSection {
  id: number;
  section_id: number;
  comment: string;
  topic_ids: number[];
}

@Injectable()
export class ScreeningToolFormAdaptor extends AbstractRequestFormAdaptor<ScreeningToolForm, AssessmentFormResponse> {
  name = 'screening-tool';

  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private navCtrl = inject(NavController);
  private profileAdaptor = inject(ProfileFetchAdaptor);
  private alertCtrl = inject(AlertController);
  // private warnUnload = inject(WarnUnloadService);

  override onRequest(formData: ScreeningToolForm): Observable<AssessmentFormResponse> {
    const { assessment_id, submit, ...assessmentData } = formData;

    return this.auth.token$.pipe(
      first(),
      switchMap((token) =>
        this.http.put<AssessmentFormResponse>(
          `/assessments/${assessment_id}.json`,
          {
            assessment: assessmentData, // assessment_id is removed from this object
            type: submit ? 'submit' : 'save',
          },
          { context: new HttpContext().set(AUTH_TOKEN, token) },
        ),
      ),
    );
  }

  override onSuccess(_formData: ScreeningToolForm, res: AssessmentFormResponse) {
    // this.warnUnload.set(false);

    this.profileAdaptor.data$.pipe(take(1)).subscribe((profile) => {
      const existingAssessment = profile.assessments.find((a) => a.id === res.id);
      const hasChanges = !existingAssessment || JSON.stringify(existingAssessment) !== JSON.stringify(res);

      if (!hasChanges) return; // Prevent unnecessary updates

      const newProfileObject = {
        ...profile,
        assessments: profile.assessments.map((assessment) =>
          assessment.id === res.id ? { ...assessment, ...res } : assessment,
        ),
      };

      setTimeout(() => {
        this.profileAdaptor.dataSubject.next(newProfileObject);
      }, 0);
    });

    this.navCtrl.navigateRoot(['survey-complete']);
  }

  override async onError(errorRes: unknown) {
    let error = 'Unknown error';
    if (errorRes instanceof HttpErrorResponse) {
      error = errorRes.error?.errors || error;
    }
    const alert = await this.alertCtrl.create({ message: error, buttons: ['OK'] });
    alert.present();
  }
}
